import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["select", "modal", "input", "filter", "listItem", "displayedValue", "typeOfMe", "stateRegisterNumber", "detailsModal", "unfitReason", "modificationCombobox", "modificationSuggestions", "modificationSuggestionsInput"]

  connect() {
    this.modal = new bootstrap.Modal(this.modalTarget)
    this.detailsModal = new bootstrap.Modal(this.detailsModalTarget)

    this.modificationSuggestions = []
    this.activeModificationIndex = -1

    this.setupEventListeners()

    const selected = this.listItemTargets.find(item => item.classList.contains("-selected"))
    this.setModificationSuggestions(selected || null)
  }

  setupEventListeners() {
    // Filter input events
    this.filterTarget.addEventListener('keyup', this.filterItems.bind(this))
    this.filterTarget.addEventListener('search', this.filterItems.bind(this))
    this.filterTarget.addEventListener('blur', this.filterItems.bind(this))

    // List item events
    this.listItemTargets.forEach(item => {
      const detailsBtn = item.querySelector('.details')
      if (detailsBtn) {
        detailsBtn.addEventListener('click', (e) => this.showDetails(e, item))
      }

      const content = item.querySelector('.content')
      if (content) {
        content.addEventListener('click', (e) => this.selectItem(e, item))
      }
    })
  }

  filterItems(event) {
    const value = event.target.value.toLowerCase()
    this.listItemTargets.forEach(item => {
      const index = [
        item.dataset.typeOfMe.toLowerCase(),
        item.dataset.stateRegisterNumber.toLowerCase(),
        item.dataset.designation.toLowerCase()
      ].join("\n")

      if (index.includes(value)) {
        item.classList.remove('-filtered')
      } else {
        item.classList.add('-filtered')
      }
    })
  }

  showDetails(event, item) {
    event.preventDefault()
    const data = { ...item.dataset }
    const body = this.renderDetailsModalBody(data)

    this.detailsModalTarget.querySelector('#me_details_label').innerText = data.stateRegisterNumber
    this.detailsModalTarget.querySelector('.modal-body').innerHTML = body

    this.modal.hide()
    this.detailsModal.show()

    return false
  }

  renderDetailsModalBody(details) {
    let html = `<div class="me_details-content">`
    if (details.image) {
      html += `<img src="${details.image}" />`
    }
    if (details.designation) {
      html += `<h3>${details.designation}</h3>`
    }
    html += `
      <div class="attr">
        <p class="label">Наименование, тип СИ</p>
        <p class="value">${details.typeOfMe || '–'}</p>
      </div>
      <div class="attr">
        <p class="label">Производитель</p>
        <p class="value">${details.manufacturer || '–'}</p>
      </div>
      <div class="attr">
        <p class="label">Методология</p>
        <p class="value">${details.methodology || '–'}</p>
      </div>
      <div class="attr">
        <p class="label">Интервал</p>
        <p class="value">
          ${details.intervalCold || '–'}/${details.intervalHot || '–'}
          лет (ХВС/ГВС)
        </p>
      </div>
    </div>`

    return html
  }

  selectItem(event, item) {
    event.preventDefault()
    const typeOfMe = item.dataset.typeOfMe
    const stateRegisterNumber = item.dataset.stateRegisterNumber

    this.typeOfMeTarget.value = typeOfMe
    this.stateRegisterNumberTarget.value = stateRegisterNumber
    this.displayedValueTarget.innerText = stateRegisterNumber + ' ' + typeOfMe

    this.setModificationSuggestions(item)

    this.modal.hide()

    this.listItemTargets.forEach(item => item.classList.remove('-selected'))
    item.classList.add('-selected')
  }

  // Stores the suggestion list for the currently selected equipment. The list
  // powers the modification combobox: a text input the verifier can either type
  // freely into or pick a value from a filtered dropdown.
  setModificationSuggestions(item) {
    let suggestions = []
    if (item) {
      try {
        suggestions = JSON.parse(item.dataset.modificationSuggestions || "[]")
      } catch (e) {
        suggestions = []
      }
    }
    this.modificationSuggestions = suggestions
    this.hideModificationMenu()
  }

  showModificationMenu() {
    this.renderModificationMenu(this.modificationSuggestionsInputTarget.value)
  }

  renderModificationMenu(filter = "") {
    const menu = this.modificationSuggestionsTarget
    const needle = filter.trim().toLowerCase()
    const matches = this.modificationSuggestions.filter(value =>
      value.toLowerCase().includes(needle)
    )

    if (matches.length === 0) {
      this.hideModificationMenu()
      return
    }

    const items = matches.map(value => {
      const li = document.createElement("li")
      const button = document.createElement("button")
      button.type = "button"
      button.className = "dropdown-item"
      button.textContent = value
      button.dataset.value = value
      // mousedown fires before the input's blur, so preventing its default
      // keeps focus on the input and lets the click register the selection.
      button.addEventListener("mousedown", (event) => {
        event.preventDefault()
        this.selectModification(value)
      })
      li.appendChild(button)
      return li
    })

    menu.replaceChildren(...items)
    this.activeModificationIndex = -1
    menu.classList.add("show")
  }

  selectModification(value) {
    this.modificationSuggestionsInputTarget.value = value
    this.hideModificationMenu()
  }

  hideModificationMenu() {
    this.modificationSuggestionsTarget.classList.remove("show")
    this.activeModificationIndex = -1
  }

  modificationKeydown(event) {
    const menu = this.modificationSuggestionsTarget
    const items = Array.from(menu.querySelectorAll(".dropdown-item"))

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        if (!menu.classList.contains("show")) {
          this.showModificationMenu()
        } else {
          this.moveModificationActive(1, items)
        }
        break
      case "ArrowUp":
        event.preventDefault()
        this.moveModificationActive(-1, items)
        break
      case "Enter":
        if (menu.classList.contains("show") && items[this.activeModificationIndex]) {
          event.preventDefault()
          this.selectModification(items[this.activeModificationIndex].dataset.value)
        }
        break
      case "Escape":
        this.hideModificationMenu()
        break
    }
  }

  moveModificationActive(delta, items) {
    if (items.length === 0) return
    this.activeModificationIndex =
      (this.activeModificationIndex + delta + items.length) % items.length
    items.forEach((el, index) => {
      el.classList.toggle("active", index === this.activeModificationIndex)
    })
    items[this.activeModificationIndex].scrollIntoView({ block: "nearest" })
  }

  openModal(event) {
    event.preventDefault()
    this.modal.show()
  }

  closeModal(event) {
    if (event) event.preventDefault()
    this.modal.hide()
  }

  toggleUnfit(event) {
    const unfit = event.target.value === "true"
    this.unfitReasonTarget.classList.toggle("d-none", !unfit)
  }
}
